/**
 * Accessibility Utilities
 * Provides comprehensive accessibility support for the application
 */

// ARIA Labels and Descriptions
export const ARIA_LABELS = {
  // Navigation
  MAIN_NAV: 'Main navigation',
  SIDE_NAV: 'Side navigation',
  BREADCRUMB: 'Breadcrumb navigation',
  
  // Dashboard
  DASHBOARD: 'Analytics dashboard',
  METRIC_CARD: 'Metric card',
  CHART_CONTAINER: 'Chart container',
  DATA_TABLE: 'Data table',
  
  // Forms
  FORM_SECTION: 'Form section',
  INPUT_FIELD: 'Input field',
  SUBMIT_BUTTON: 'Submit button',
  CANCEL_BUTTON: 'Cancel button',
  
  // Actions
  EDIT_BUTTON: 'Edit button',
  DELETE_BUTTON: 'Delete button',
  SAVE_BUTTON: 'Save button',
  CLOSE_BUTTON: 'Close button',
  
  // Status
  LOADING: 'Loading',
  SUCCESS: 'Success',
  ERROR: 'Error',
  WARNING: 'Warning',
  
  // Social Media
  FACEBOOK_POST: 'Facebook post',
  INSTAGRAM_POST: 'Instagram post',
  SOCIAL_METRIC: 'Social media metric',
  
  // Charts
  LINE_CHART: 'Line chart',
  BAR_CHART: 'Bar chart',
  PIE_CHART: 'Pie chart',
  FUNNEL_CHART: 'Conversion funnel chart'
};

// Keyboard Navigation
export const KEYBOARD_KEYS = {
  ENTER: 'Enter',
  SPACE: ' ',
  ESCAPE: 'Escape',
  TAB: 'Tab',
  ARROW_UP: 'ArrowUp',
  ARROW_DOWN: 'ArrowDown',
  ARROW_LEFT: 'ArrowLeft',
  ARROW_RIGHT: 'ArrowRight',
  HOME: 'Home',
  END: 'End',
  PAGE_UP: 'PageUp',
  PAGE_DOWN: 'PageDown'
};

// Focus Management
export class FocusManager {
  private static focusableSelectors = [
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'a[href]',
    '[tabindex]:not([tabindex="-1"])',
    '[contenteditable="true"]'
  ].join(', ');

  /**
   * Get all focusable elements within a container
   */
  static getFocusableElements(container: HTMLElement): HTMLElement[] {
    return Array.from(container.querySelectorAll(this.focusableSelectors)) as HTMLElement[];
  }

  /**
   * Trap focus within a container
   */
  static trapFocus(container: HTMLElement, event: KeyboardEvent): void {
    const focusableElements = this.getFocusableElements(container);
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.key === KEYBOARD_KEYS.TAB) {
      if (event.shiftKey) {
        if (document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    }
  }

  /**
   * Focus first focusable element in container
   */
  static focusFirstElement(container: HTMLElement): void {
    const focusableElements = this.getFocusableElements(container);
    if (focusableElements.length > 0) {
      focusableElements[0].focus();
    }
  }

  /**
   * Focus last focusable element in container
   */
  static focusLastElement(container: HTMLElement): void {
    const focusableElements = this.getFocusableElements(container);
    if (focusableElements.length > 0) {
      focusableElements[focusableElements.length - 1].focus();
    }
  }

  /**
   * Store current focus and restore it later
   */
  static storeFocus(): HTMLElement | null {
    return document.activeElement as HTMLElement;
  }

  /**
   * Restore previously stored focus
   */
  static restoreFocus(element: HTMLElement | null): void {
    if (element && typeof element.focus === 'function') {
      element.focus();
    }
  }
}

// Screen Reader Support
export class ScreenReader {
  /**
   * Announce message to screen readers
   */
  static announce(message: string, priority: 'polite' | 'assertive' = 'polite'): void {
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', priority);
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'sr-only';
    announcement.textContent = message;

    document.body.appendChild(announcement);

    // Remove after announcement
    setTimeout(() => {
      document.body.removeChild(announcement);
    }, 1000);
  }

  /**
   * Announce loading state
   */
  static announceLoading(message: string = 'Loading'): void {
    this.announce(message, 'polite');
  }

  /**
   * Announce success
   */
  static announceSuccess(message: string): void {
    this.announce(message, 'polite');
  }

  /**
   * Announce error
   */
  static announceError(message: string): void {
    this.announce(message, 'assertive');
  }

  /**
   * Announce form validation errors
   */
  static announceValidationErrors(errors: string[]): void {
    const message = `Validation errors: ${errors.join(', ')}`;
    this.announceError(message);
  }
}

// ARIA Helpers
export const ARIA_HELPERS = {
  /**
   * Generate unique ID for ARIA relationships
   */
  generateId: (prefix: string = 'aria'): string => {
    return `${prefix}-${Math.random().toString(36).substr(2, 9)}`;
  },

  /**
   * Create ARIA describedby relationship
   */
  createDescribedBy: (element: HTMLElement, description: string): void => {
    const id = ARIA_HELPERS.generateId('desc');
    const descElement = document.createElement('div');
    descElement.id = id;
    descElement.className = 'sr-only';
    descElement.textContent = description;
    
    document.body.appendChild(descElement);
    element.setAttribute('aria-describedby', id);
  },

  /**
   * Create ARIA labelledby relationship
   */
  createLabelledBy: (element: HTMLElement, label: string): void => {
    const id = ARIA_HELPERS.generateId('label');
    const labelElement = document.createElement('div');
    labelElement.id = id;
    labelElement.className = 'sr-only';
    labelElement.textContent = label;
    
    document.body.appendChild(labelElement);
    element.setAttribute('aria-labelledby', id);
  },

  /**
   * Set ARIA live region
   */
  setLiveRegion: (element: HTMLElement, priority: 'polite' | 'assertive' = 'polite'): void => {
    element.setAttribute('aria-live', priority);
    element.setAttribute('aria-atomic', 'true');
  }
};

// Keyboard Navigation Helpers
export const KEYBOARD_HELPERS = {
  /**
   * Handle arrow key navigation in lists
   */
  handleArrowNavigation: (
    event: KeyboardEvent,
    currentIndex: number,
    totalItems: number,
    onNavigate: (index: number) => void
  ): boolean => {
    switch (event.key) {
      case KEYBOARD_KEYS.ARROW_UP:
      case KEYBOARD_KEYS.ARROW_LEFT:
        event.preventDefault();
        const prevIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        onNavigate(prevIndex);
        return true;
      
      case KEYBOARD_KEYS.ARROW_DOWN:
      case KEYBOARD_KEYS.ARROW_RIGHT:
        event.preventDefault();
        const nextIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        onNavigate(nextIndex);
        return true;
      
      case KEYBOARD_KEYS.HOME:
        event.preventDefault();
        onNavigate(0);
        return true;
      
      case KEYBOARD_KEYS.END:
        event.preventDefault();
        onNavigate(totalItems - 1);
        return true;
      
      default:
        return false;
    }
  },

  /**
   * Handle enter/space key activation
   */
  handleActivation: (
    event: KeyboardEvent,
    onActivate: () => void
  ): boolean => {
    if (event.key === KEYBOARD_KEYS.ENTER || event.key === KEYBOARD_KEYS.SPACE) {
      event.preventDefault();
      onActivate();
      return true;
    }
    return false;
  },

  /**
   * Handle escape key
   */
  handleEscape: (
    event: KeyboardEvent,
    onEscape: () => void
  ): boolean => {
    if (event.key === KEYBOARD_KEYS.ESCAPE) {
      event.preventDefault();
      onEscape();
      return true;
    }
    return false;
  }
};

// Color Contrast Utilities
export const CONTRAST_HELPERS = {
  /**
   * Calculate relative luminance
   */
  getRelativeLuminance: (r: number, g: number, b: number): number => {
    const [rs, gs, bs] = [r, g, b].map(c => {
      c = c / 255;
      return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
  },

  /**
   * Calculate contrast ratio
   */
  getContrastRatio: (l1: number, l2: number): number => {
    const lighter = Math.max(l1, l2);
    const darker = Math.min(l1, l2);
    return (lighter + 0.05) / (darker + 0.05);
  },

  /**
   * Check if contrast meets WCAG AA standards
   */
  meetsWCAGAA: (contrastRatio: number, isLargeText: boolean = false): boolean => {
    return isLargeText ? contrastRatio >= 3 : contrastRatio >= 4.5;
  }
};

// Semantic HTML Helpers
export const SEMANTIC_HELPERS = {
  /**
   * Get appropriate heading level
   */
  getHeadingLevel: (currentLevel: number, maxLevel: number = 6): number => {
    return Math.min(Math.max(currentLevel, 1), maxLevel);
  },

  /**
   * Create semantic landmark
   */
  createLandmark: (role: string, label: string): HTMLElement => {
    const element = document.createElement('div');
    element.setAttribute('role', role);
    element.setAttribute('aria-label', label);
    return element;
  }
};

// Skip Links
export const SKIP_LINKS = {
  /**
   * Create skip link
   */
  createSkipLink: (targetId: string, text: string): HTMLAnchorElement => {
    const link = document.createElement('a');
    link.href = `#${targetId}`;
    link.textContent = text;
    link.className = 'skip-link';
    link.setAttribute('aria-label', `Skip to ${text.toLowerCase()}`);
    return link;
  },

  /**
   * Add skip links to page
   */
  addSkipLinks: (): void => {
    const skipLinks = [
      { target: 'main-content', text: 'Skip to main content' },
      { target: 'navigation', text: 'Skip to navigation' },
      { target: 'search', text: 'Skip to search' }
    ];

    const skipContainer = document.createElement('div');
    skipContainer.className = 'skip-links-container';

    skipLinks.forEach(({ target, text }) => {
      const link = SKIP_LINKS.createSkipLink(target, text);
      skipContainer.appendChild(link);
    });

    document.body.insertBefore(skipContainer, document.body.firstChild);
  }
};

export default {
  ARIA_LABELS,
  KEYBOARD_KEYS,
  FocusManager,
  ScreenReader,
  ARIA_HELPERS,
  KEYBOARD_HELPERS,
  CONTRAST_HELPERS,
  SEMANTIC_HELPERS,
  SKIP_LINKS
};
